// Setshaba dosing schedule import → normalized DoseRow schema
// Accepts: JSON (array or {doses:[]}), CSV/TSV, or pipe/tab/multi-space tables
// with header aliases used in Setshaba protocol books.

export interface DoseRow {
  visit: string;
  day: string;
  dose: string;
  route: string;
  product: string;
}

const HEADER_ALIASES: Record<keyof DoseRow, string[]> = {
  visit: ["visit", "visit name", "visit #", "visit no", "visit number", "timepoint", "time point"],
  day: ["day", "study day", "day #", "day no", "nominal day"],
  product: ["product", "imp", "investigational product", "vaccine", "drug", "treatment", "study drug", "study treatment", "intervention"],
  dose: ["dose", "dose amount", "dose level", "volume", "amount", "strength"],
  route: ["route", "route of administration", "roa", "administration"],
};

const routeCanonical = (v: string): string => {
  const s = v.trim().toLowerCase();
  if (!s) return "";
  if (/^im\b|intramuscular/.test(s)) return "Intramuscular";
  if (/^iv\b|intravenous/.test(s)) return "Intravenous";
  if (/^sc\b|subcut/.test(s)) return "Subcutaneous";
  if (/oral|^po\b|by mouth/.test(s)) return "Oral";
  if (/topical/.test(s)) return "Topical";
  if (/intranasal|^in\b/.test(s)) return "Intranasal";
  return v.trim();
};

const cleanCell = (v: unknown): string => {
  if (v === null || v === undefined) return "";
  return String(v).replace(/\s+/g, " ").trim();
};

const dayCanonical = (v: string): string => {
  const s = v.trim();
  if (!s || /^(n\/?a|—|-|null|none)$/i.test(s)) return "";
  // "Day 1" -> "1", keep ranges like "1-3"
  const m = s.match(/^day\s*(-?\d+(?:\s*[-–]\s*\d+)?)$/i);
  if (m) return m[1].replace(/\s+/g, "");
  return s;
};

const visitCanonical = (v: string): string => {
  const s = v.trim();
  if (!s) return "";
  // "V1" / "1" -> "Visit 1"; leave "Visit 403" alone
  if (/^visit\s+/i.test(s)) return s.replace(/^visit\s+/i, "Visit ");
  const m = s.match(/^v(?:isit)?[\s\-#]*(\d+[a-z]?)$/i);
  if (m) return `Visit ${m[1]}`;
  if (/^\d+$/.test(s)) return `Visit ${s}`;
  return s;
};

const normalizeRow = (r: Partial<DoseRow>): DoseRow => ({
  visit: visitCanonical(r.visit ?? ""),
  day: dayCanonical(r.day ?? ""),
  dose: cleanCell(r.dose),
  route: routeCanonical(r.route ?? ""),
  product: cleanCell(r.product),
});

const mapHeaderIndex = (headers: string[]): Partial<Record<keyof DoseRow, number>> => {
  const idx: Partial<Record<keyof DoseRow, number>> = {};
  const norm = headers.map((h) => h.trim().toLowerCase());
  (Object.keys(HEADER_ALIASES) as (keyof DoseRow)[]).forEach((key) => {
    for (const alias of HEADER_ALIASES[key]) {
      const i = norm.indexOf(alias);
      if (i >= 0) { idx[key] = i; return; }
    }
    // fuzzy contains match
    for (let i = 0; i < norm.length; i++) {
      if (HEADER_ALIASES[key].some((a) => norm[i].includes(a))) { idx[key] = i; return; }
    }
  });
  return idx;
};

const splitLine = (line: string, delim: "|" | "\t" | "csv" | "ws"): string[] => {
  if (delim === "|") return line.split("|").map((c) => c.trim()).filter((_, i, arr) => !(i === 0 && arr[0] === "") && !(i === arr.length - 1 && arr[arr.length - 1] === ""));
  if (delim === "\t") return line.split("\t").map((c) => c.trim());
  if (delim === "ws") return line.split(/\s{2,}/).map((c) => c.trim()).filter(Boolean);
  // csv - naive but handles quoted fields
  const out: string[] = [];
  let cur = ""; let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
    else if (c === "," && !inQ) { out.push(cur.trim()); cur = ""; }
    else cur += c;
  }
  out.push(cur.trim());
  return out;
};

const detectDelim = (line: string): "|" | "\t" | "csv" | "ws" => {
  if (line.includes("|")) return "|";
  if (line.includes("\t")) return "\t";
  if (/,/.test(line) && line.split(",").length >= 3) return "csv";
  return "ws";
};

const parseTabular = (text: string): DoseRow[] => {
  const lines = text.split(/\r?\n/).map((l) => l.trim())
    .filter((l) => l && !/^[-=|+\s]+$/.test(l)); // drop separator rows
  if (lines.length < 2) return [];
  const delim = detectDelim(lines[0]);
  const rows = lines.map((l) => splitLine(l, delim));
  const headerIdx = mapHeaderIndex(rows[0]);
  const hasHeader = Object.keys(headerIdx).length >= 2;
  const dataRows = hasHeader ? rows.slice(1) : rows;
  const map: Partial<Record<keyof DoseRow, number>> = hasHeader
    ? headerIdx
    : { visit: 0, day: 1, product: 2, dose: 3, route: 4 };
  const out: DoseRow[] = [];
  for (const cells of dataRows) {
    const row = normalizeRow({
      visit: map.visit !== undefined ? cells[map.visit] : "",
      day: map.day !== undefined ? cells[map.day] : "",
      product: map.product !== undefined ? cells[map.product] : "",
      dose: map.dose !== undefined ? cells[map.dose] : "",
      route: map.route !== undefined ? cells[map.route] : "",
    });
    if (row.visit || row.product || row.dose) out.push(row);
  }
  return out;
};

const parseJson = (text: string): DoseRow[] | null => {
  try {
    const j = JSON.parse(text);
    const arr = Array.isArray(j) ? j : Array.isArray(j?.doses) ? j.doses : null;
    if (!arr) return null;
    return arr.map((r: any) => normalizeRow({
      visit: r.visit ?? r.Visit ?? r.visit_name ?? "",
      day: r.day !== undefined && r.day !== null ? String(r.day) : (r.Day ?? ""),
      product: r.product ?? r.Product ?? r.imp ?? r.drug ?? r.vaccine ?? "",
      dose: r.dose ?? r.Dose ?? r.amount ?? r.volume ?? "",
      route: r.route ?? r.Route ?? r.roa ?? "",
    })).filter((r: DoseRow) => r.visit || r.product || r.dose);
  } catch {
    return null;
  }
};

export interface ImportResult {
  rows: DoseRow[];
  format: "json" | "table" | "empty";
}

export function parseSetshabaDosing(input: string): ImportResult {
  const text = input.trim();
  if (!text) return { rows: [], format: "empty" };
  const j = parseJson(text);
  if (j && j.length) return { rows: j, format: "json" };
  const rows = parseTabular(text);
  return { rows, format: rows.length ? "table" : "empty" };
}
